import { MemberTypes, SdbMember } from "@custom-types/member-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import {
  ConvertCompanyMemberBaseToSdbMember,
  ConvertCompanyMemberDetailsToSdbMember,
  ConvertGroupMemberToSdbMember,
  ConvertProjectMemberBaseToSdbMember,
  ConvertSdbTeamMemberBaseToSdbMember,
  ConvertUserAsCompanyManagerToSdbMember,
  ConvertUserAsProjectManagerToSdbMember,
} from "@store/members/members-slice-helper";

// TODO: ST-646 This should not be needed anymore after 646 is implemented
/**
 * Converts a project member from FaroCoreApiTypes.ProjectMemberBase to MemberTypes.
 * Adds a default role of 'member' to ensure proper state management in the store.
 */
export function convertToMemberTypes(
  member: FaroCoreApiTypes.ProjectMemberBase | FaroCoreApiTypes.UserAsManager
): MemberTypes {
  const convertedMember: MemberTypes = {
    identity: member.identity,
    email: member.email,
    role: "MEMBER",
    kind: "user",
  };

  return convertedMember;
}

// TODO: ST-646 This should not be needed anymore after 646 is implemented
/**
 * Converts a team to MemberTypes for the members store.
 */
export function convertToTeamMemberTypes(
  team: FaroCoreApiTypes.Team,
  role?: FaroCoreApiTypes.AssignmentRoleBySubjectType["company"]
): MemberTypes {
  const convertedToTeamMember: MemberTypes = {
    id: team.id,
    identity: team.identity,
    email: team.email,
    role: role || "MEMBER",
    kind: "user-group",
    name: team.name,
    permissions: [],
  };

  return convertedToTeamMember;
}

/** Convert CompanyMemberBase to SdbMember */
export function convertCompanyMemberBaseToSdbMember({
  member,
  companyId,
}: ConvertCompanyMemberBaseToSdbMember): SdbMember {
  return {
    id: member.id,
    email: member.email,
    identity: member.identity,
    name: member.name ?? null,
    firstName: member.firstName ?? null,
    lastName: member.lastName ?? null,
    thumbnailUrl: member.thumbnailUrl,
    permissions:
      "permissions" in member && member.permissions ? member.permissions : null,
    kind: member.kind ?? "user",
    role: member.role,
    companyProps: {
      [companyId]: { companyId, role: member.role, userRole: null },
    },
    projectProps: null,
    groupProps: null,
  };
}

/** Convert CompanyMemberDetails to SdbMember */
export function convertCompanyMemberDetailsToSdbMember({
  member,
  companyId,
}: ConvertCompanyMemberDetailsToSdbMember): SdbMember {
  return {
    id: member.id,
    email: member.email,
    identity: member.identity,
    name: member.name ?? null,
    firstName: member.firstName ?? null,
    lastName: member.lastName ?? null,
    thumbnailUrl: member.thumbnailUrl,
    permissions:
      "permissions" in member && member.permissions ? member.permissions : null,
    kind: member.kind ?? "user",
    role: member.role,
    companyProps: {
      [companyId]: { companyId, role: member.role, userRole: member.userRole },
    },
    projectProps: null,
    groupProps: null,
  };
}

/** Convert ProjectMemberBase to SdbMember */
export function convertProjectMemberBaseToSdbMember({
  member,
  projectId,
}: ConvertProjectMemberBaseToSdbMember): SdbMember {
  return {
    id: member.id,
    email: member.email,
    identity: member.identity,
    name: member.name ?? null,
    firstName: member.firstName ?? null,
    lastName: member.lastName ?? null,
    thumbnailUrl: member.thumbnailUrl,
    permissions: null,
    kind: member.kind ?? "user",
    role: member.role,
    companyProps: null,
    projectProps: { [projectId]: { projectId, role: member.role } },
    groupProps: null,
  };
}

/** Convert GroupMember to SdbMember */
export function convertGroupMemberToSdbMember({
  member,
  companyId,
  groupId,
}: ConvertGroupMemberToSdbMember): SdbMember {
  return {
    id: member.id,
    email: member.email,
    identity: member.identity,
    name: member.name ?? null,
    firstName: member.firstName ?? null,
    lastName: member.lastName ?? null,
    thumbnailUrl: member.thumbnailUrl,
    permissions: null,
    kind: "user",
    role: member.role,
    companyProps: {
      [companyId]: { companyId, role: member.role, userRole: null },
    },
    projectProps: null,
    groupProps: {
      [groupId]: {
        groupId,
        dateJoined: member.dateJoined,
      },
    },
  };
}

/** Convert SdbTeamMember to SdbMember */
export function convertSdbTeamMemberBaseToSdbMember({
  member,
}: ConvertSdbTeamMemberBaseToSdbMember): SdbMember {
  return {
    id: member.id,
    email: member.email,
    identity: member.identity,
    name: member.name ?? null,
    firstName: member.firstName ?? null,
    lastName: member.lastName ?? null,
    thumbnailUrl: member.thumbnailUrl,
    permissions: null,
    kind: "user-group",
    role: member.role,
    companyProps: null,
    projectProps: null,
    groupProps: null,
  };
}

/** Convert UserAsCompanyManager to SdbMember */
export function convertUserAsCompanyManagerToSdbMember({
  member,
  role,
  companyId,
}: ConvertUserAsCompanyManagerToSdbMember): SdbMember {
  return {
    id: member.id,
    email: member.email,
    identity: member.identity,
    name: member.name ?? null,
    firstName: member.firstName ?? null,
    lastName: member.lastName ?? null,
    thumbnailUrl: member.thumbnailUrl,
    permissions: null,
    kind: member.kind ?? "user",
    role,
    companyProps: { [companyId]: { companyId, role, userRole: null } },
    projectProps: null,
    groupProps: null,
  };
}

/** Convert UserAsCProjectManager to SdbMember */
export function convertUserAsProjectManagerToSdbMember({
  member,
  role,
  projectId,
}: ConvertUserAsProjectManagerToSdbMember): SdbMember {
  return {
    id: member.id,
    email: member.email,
    identity: member.identity,
    name: member.name ?? null,
    firstName: member.firstName ?? null,
    lastName: member.lastName ?? null,
    thumbnailUrl: member.thumbnailUrl,
    permissions: null,
    kind: member.kind ?? "user",
    role,
    companyProps: null,
    projectProps: { [projectId]: { projectId, role } },
    groupProps: null,
  };
}
